import { Table } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { FileDeletionType } from '../../../../../../../../../backend/src/types/types';
import { formatNumber } from '../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { TablePagination } from '@/new/components/table/table-pagination';

export type LogEventType = 'upload' | 'deletion';

export interface BaseLogEvent {
  id: string;
  type: LogEventType;
  date: string;
  userEmail: string;
}

export interface UploadLogEvent extends BaseLogEvent {
  type: 'upload';
  attempted: number;
  successful: number;
  skipped: number;
}

export interface DeletionLogEvent extends BaseLogEvent {
  type: 'deletion';
  count: number;
  deletionType: FileDeletionType;
}

export type LogEvent = UploadLogEvent | DeletionLogEvent;

interface FileHistoryLogProps {
  matterId: string;
  isLogLoading: boolean;
  logEvents: LogEvent[];
  logTable: Table<LogEvent>;
}

export function FileHistoryLog({ matterId, isLogLoading, logEvents, logTable }: FileHistoryLogProps) {
  if (isLogLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (logEvents.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-500">No events to display</div>;
  }

  return (
    <>
      <div className="divide-y divide-gray-100">
        {logTable.getRowModel().rows.map((row) => {
          const event = row.original;
          return (
            <Link
              to={
                event.type === 'upload'
                  ? `/matters/${matterId}/settings/file-history/file-log/uploads/${event.id}`
                  : `/matters/${matterId}/settings/file-history/file-log/deletions/${event.id}`
              }
              key={`${event.type}-${event.id}`}
              className="flex cursor-pointer items-center gap-4 px-2 py-4 transition-colors hover:bg-gray-50"
              onClick={() =>
                trackCustomEvent(
                  event.type === 'upload'
                    ? TrackingEventsV2.FileHistoryClickUploadEvent
                    : TrackingEventsV2.FileHistoryClickDeletionEvent,
                  { matterId, eventId: event.id },
                )
              }
            >
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon
                      className={`size-3.5 text-gray-800 ${event.type === 'upload' ? 'lucide--upload' : 'lucide--trash-2'}`}
                    />
                    <span className="font-medium text-gray-800">
                      {event.type === 'upload' ? 'Files Uploaded' : 'Files Deleted'}
                    </span>
                    <span className="text-sm text-gray-500">by {event.userEmail}</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(event.date, true, true)}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {event.type === 'upload' ? (
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        <span className="font-medium text-blue-900">{formatNumber(event.attempted)} attempted</span>
                      </div>
                      <Icon className="lucide--arrow-right h-3 w-3 text-gray-300" />
                      <div className="flex items-center overflow-hidden rounded-md">
                        <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                          <span className="font-medium text-green-900">
                            {formatNumber(event.successful)} successfully uploaded
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
                          <span className="font-medium text-amber-900">{formatNumber(event.skipped)} skipped</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center overflow-hidden rounded-md text-xs">
                      <div className="flex items-center gap-1.5 bg-red-50 px-2 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
                        <span className="font-medium text-red-700">
                          {formatNumber(event.count)} deleted{' '}
                          {event.deletionType === FileDeletionType.Staged ? 'before submission' : 'after submission'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {logEvents.length > 0 && <TablePagination table={logTable} plural="events" />}
    </>
  );
}
